import React from 'react';
import { Spinner } from './components/Spinner';
import { ExportModal } from './components/ExportModal';
import { ConfirmModal } from './components/ConfirmModal';
import { DuplicateReceiptModal } from './components/DuplicateReceiptModal';
import { ReceiptsView } from './components/ReceiptsView';
import { useReceiptsManager } from './hooks/useReceiptsManager';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { ReceiptIcon } from './components/icons/ReceiptIcon';
import { DashboardIcon } from './components/icons/DashboardIcon';
import { PriceTagIcon } from './components/icons/PriceTagIcon';
import { formatDate } from './services/utils';
import { FullScreenDropZone } from './components/FullScreenDropZone';
import { DropdownMenu } from './components/DropdownMenu';
import { Login } from './components/Login';
import { useAuth } from './hooks/useAuth';
import { SettingsProvider } from './contexts/SettingsContext';
import { DateRangeFilter } from './components/DateRangeFilter';
import { AuthProvider } from './contexts/AuthContext';
import { Dashboard } from './components/Dashboard';
import { PriceTracker } from './components/PriceTracker';
import { DataManagement } from './components/DataManagement';
import { AppSettings } from './components/AppSettings';
import { LogoutIcon } from './components/icons/LogoutIcon';
import { ServerIcon } from './components/icons/ServerIcon';
import { ProcessingView } from './components/ProcessingView';

const MainApp: React.FC = () => {
  const { logout } = useAuth();
  const receiptsManager = useReceiptsManager();
  
  const {
    currentView,
    setCurrentView,
    duplicateInfo,
    handleDuplicateConfirm,
    handleDuplicateSkip,
    receiptToDelete,
    handleConfirmDelete,
    handleCancelDelete,
    isExportModalOpen,
    setIsExportModalOpen,
    handleExport,
    filters,
    setFilters
  } = receiptsManager;

  const { isDragging } = useDragAndDrop(receiptsManager.handleFilesSelected);

  const NavButton: React.FC<{ view: ReturnType<typeof useReceiptsManager>['currentView']; label: string; icon: React.ReactNode }> = ({ view, label, icon }) => (
    <button
      onClick={() => setCurrentView(view)}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${currentView === view ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}
    >
      {icon}
      {label}
    </button>
  );
  
  const handleDateRangeChange = (range: { from: string | null; to: string | null }) => {
      setFilters(prev => ({
          ...prev,
          dateFrom: range.from,
          dateTo: range.to,
      }));
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'receipts':
        return <ReceiptsView receiptsManager={receiptsManager} />;
      case 'dashboard':
        return <Dashboard 
                  filters={filters} 
                  setFilters={setFilters} 
                  filterOptions={receiptsManager.filterOptions} 
                />;
      case 'pricetracker':
        return <PriceTracker dateFrom={filters.dateFrom} dateTo={filters.dateTo} />;
      case 'processing':
        return <ProcessingView />;
      case 'dataManagement':
        return <DataManagement />;
      case 'appSettings':
        return <AppSettings />;
      default:
        return null;
    }
  }

  const showDateFilter = currentView === 'dashboard' || currentView === 'pricetracker' || currentView === 'receipts';

  return (
    <div className="bg-gray-100 min-h-screen font-sans">
      {isDragging && <FullScreenDropZone />}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white p-6 rounded-xl shadow-md">
          <header className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-4 border-b pb-4">
            <nav className="flex items-center gap-2 flex-wrap">
              <NavButton view="receipts" label="Přehled účtenek" icon={<ReceiptIcon className="w-5 h-5"/>} />
              <NavButton view="dashboard" label="Dashboard" icon={<DashboardIcon className="w-5 h-5"/>} />
              <NavButton view="pricetracker" label="Sledování cen" icon={<PriceTagIcon className="w-5 h-5"/>} />
              <NavButton view="processing" label="Zpracování" icon={<ServerIcon className="w-5 h-5"/>} />
            </nav>
            <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
              {showDateFilter && (
                  <DateRangeFilter
                      value={{ from: filters.dateFrom, to: filters.dateTo }}
                      onChange={handleDateRangeChange}
                  />
              )}
              <DropdownMenu 
                onNavigateToDataManagement={() => setCurrentView('dataManagement')}
                onNavigateToAppSettings={() => setCurrentView('appSettings')}
              />
               <button
                onClick={logout}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors text-gray-600 hover:bg-gray-200"
                title="Odhlásit se"
              >
                <LogoutIcon className="w-5 h-5" />
                <span>Odhlásit</span>
              </button>
            </div>
          </header>

          {renderCurrentView()}
        </div>
        
        {isExportModalOpen && (
            <ExportModal 
                onClose={() => setIsExportModalOpen(false)}
                onExport={handleExport}
            />
        )}
        
        <ConfirmModal
          isOpen={!!receiptToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Smazat účtenku?"
          confirmText="Smazat"
          confirmClassName="bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          {receiptToDelete && (
            <div className="text-sm text-gray-700">
              <p>Opravdu si přejete smazat následující účtenku? Tato akce je nevratná.</p>
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md space-y-2">
                <div>
                  <span className="font-semibold text-gray-500">Podnik: </span>
                  <span className="font-medium text-gray-900">{receiptToDelete.businessName || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-500">Datum: </span>
                  <span className="font-medium text-gray-900">{formatDate(receiptToDelete.receiptDate)}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-500">Částka: </span>
                  <span className="font-medium text-gray-900">{receiptToDelete.amount?.toLocaleString('cs-CZ', { style: 'currency', currency: receiptToDelete.currency.name || 'CZK' }) ?? 'N/A'}</span>
                </div>
              </div>
            </div>
          )}
        </ConfirmModal>

        {duplicateInfo && (
            <DuplicateReceiptModal
                isOpen={!!duplicateInfo}
                onConfirm={handleDuplicateConfirm}
                onSkip={handleDuplicateSkip}
                existingReceipt={duplicateInfo.existingReceipt}
                newReceipt={duplicateInfo.newReceipt}
            />
        )}
      </main>
    </div>
  );
};

const AppContent: React.FC = () => {
    const { isAuthenticated } = useAuth();

    if (!isAuthenticated) {
        return <Login />;
    }

    return (
        <SettingsProvider>
            <MainApp />
        </SettingsProvider>
    );
};

const App: React.FC = () => (
    <AuthProvider>
        <AppContent />
    </AuthProvider>
);

export default App;
